/**
 * App — wires QueryClientProvider, AuthProvider, ThemeProvider around the
 * RouterProvider. ModeProvider lives inside RootLayout because it needs
 * the router context (useLocation).
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { useMemo } from "react";

import { AuthProvider } from "@/contexts/auth/AuthContext";
import type { AuthStatus, AuthUser } from "@/contexts/auth/AuthContext";
import { ThemeProvider } from "@/contexts/theme/ThemeContext";
import { createAppRouter, createTestRouter } from "@/router";

export interface AppProps {
  /** Test-only memory router. Production uses createBrowserRouter. */
  readonly testRouter?: ReturnType<typeof createTestRouter>;
  readonly authStatus?: AuthStatus;
  readonly authUser?: AuthUser | null;
}

export default function App({
  testRouter,
  authStatus,
  authUser,
}: AppProps = {}) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, staleTime: 30_000 },
        },
      }),
    [],
  );

  const router = testRouter ?? createAppRouter();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider initialStatus={authStatus} initialUser={authUser}>
          <RouterProvider router={router} />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
